import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { AlertTriangle, ArrowLeftRight, Search, Sparkles, TrendingUp, Zap } from 'lucide-react'

const HOW_IT_WORKS = [
  {
    step: 1,
    title: 'Find Spread',
    description: 'We scan all HypurrFi markets to identify where borrow rates are lower than deposit rates.',
    color: '#A1FCE7',
  },
  {
    step: 2,
    title: 'Borrow Low',
    description: 'Borrow from the market with the lowest rate using your collateral.',
    color: '#FCE572',
  },
  {
    step: 3,
    title: 'Deposit High',
    description: 'Deposit into the higher-yielding market and pocket the spread.',
    gradient: true,
  },
]

const FEATURES = [
  {
    icon: Search,
    title: 'Cross-Protocol Scanning',
    description: 'Compares rates across Pooled, Isolated, Mewler, Earn Vaults, and USDXL.',
  },
  {
    icon: ArrowLeftRight,
    title: 'Asset Matching',
    description: 'Groups equivalent assets (stablecoins, HYPE variants) for maximum opportunity discovery.',
  },
  {
    icon: Zap,
    title: 'Live Rates',
    description: 'All rates update in real-time from on-chain data. Spreads may change rapidly.',
  },
  {
    icon: AlertTriangle,
    title: 'Risk Considerations',
    description: 'Spreads shown are indicative. Consider gas costs, liquidation risk, rate volatility, and slippage.',
  },
]

export function OpportunitiesInfoCards() {
  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* How it works */}
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex items-center gap-2 px-1">
          <Sparkles className="h-4 w-4 text-[#FBE572]" />
          <Typography variant="h4" className="font-semibold text-foreground text-sm">
            How it works
          </Typography>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {HOW_IT_WORKS.map((item, i) => (
            <Panel.Wrapper
              key={item.step}
              className="p-4"
              style={{
                animationDelay: `${i * 0.1 + 0.1}s`,
                animation: 'sweep-card-in 0.4s ease-out forwards',
                opacity: 0,
              }}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-6 w-6 items-center justify-center rounded-full font-bold text-xs"
                    style={{
                      background: item.gradient ? 'linear-gradient(135deg, #FBE572, #A1FCE7)' : `${item.color}20`,
                      color: item.gradient ? '#0E0E10' : item.color,
                    }}
                  >
                    {item.step}
                  </div>
                  <Typography
                    variant="h4"
                    className="font-semibold text-sm"
                    style={
                      item.gradient
                        ? {
                            background: 'linear-gradient(90deg, #FCE572, #A1FCE7)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }
                        : { color: item.color }
                    }
                  >
                    {item.title}
                  </Typography>
                </div>
                <Typography variant="p" className="text-muted-foreground text-xs leading-relaxed">
                  {item.description}
                </Typography>
              </div>
            </Panel.Wrapper>
          ))}
        </div>
      </div>

      {/* Details */}
      <div className="flex w-full flex-col gap-3 lg:w-72">
        <div className="flex items-center gap-2 px-1">
          <TrendingUp className="h-4 w-4 text-[#A1FCE7]" />
          <Typography variant="h4" className="font-semibold text-foreground text-sm">
            Details
          </Typography>
        </div>

        {FEATURES.map((feature, i) => (
          <Panel.Wrapper
            key={feature.title}
            className="p-4"
            style={{
              animationDelay: `${i * 0.1 + 0.2}s`,
              animation: 'sweep-card-in 0.4s ease-out forwards',
              opacity: 0,
            }}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#FBE572]/10 to-[#A1FCE7]/10">
                <feature.icon className="h-4 w-4 text-[#A1FCE7]" />
              </div>
              <div>
                <Typography variant="h4" className="font-semibold text-sm">
                  {feature.title}
                </Typography>
                <Typography variant="p" className="mt-1 text-muted-foreground text-xs leading-relaxed">
                  {feature.description}
                </Typography>
              </div>
            </div>
          </Panel.Wrapper>
        ))}
      </div>
    </div>
  )
}
